import type React from 'react';
import {
  useCallback, useContext, useEffect,
  useRef,
} from 'react';
import {
  Offcanvas,
  OffcanvasBody,
} from 'reactstrap';
import { toast } from 'react-toastify';
import type Pusher from 'pusher-js';

import { NavTable } from 'src/components/job-slide-out/components/nav-table';
import Header from 'src/components/job-slide-out/components/header';
import BasicInfo from 'src/components/job-slide-out/components/basic-info';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import {
  clearJobDetails,
  clearSlideOut, updateActivityLog, updateMessage, updateMessageOnPusher, updatePropertyEscalation, updateResidentCausedFromPusher, updateSlideOut,
  updateSlideOutData,
} from 'src/redux/jobs/slice';
import { fetchJobDetails, fetchServiceRequestDetails, fetchWorkOrderFlags } from 'src/redux/jobs/api';

import { createAbortSignal } from 'src/helpers/utils';
import { AuthContext } from 'src/context/auth-context/auth-context';
import { isEmpty } from 'lodash';
import { updateOpenJobs } from 'src/redux/open-jobs/slice';
import { usePusherConfig } from 'src/context/pusher-context';
import useFeatureFlags from 'src/hooks/use-feature-flags';
import { isMakeReadyServiceCategory } from 'src/helpers/job-slide-out';
import type { ActivityLogEntry, ActivityLogState } from 'src/types/jobs';
import type { PusherMessagePayload } from 'src/redux/jobs/reducers';


interface PusherResponse {
  event_for?: string[];
  service_request_number?: string;
  root_job_no?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

interface PusherChannel {
  bind: (event: string, callback: (data: unknown) => void) => void;
}

interface PropertyEscalation {
  property_escalation_id: string;
  is_escalated: boolean;
  reason: string | null;
}

interface PropertyEscalationUpdatedPayload {
  service_request_ids: number[];
  property_escalation: PropertyEscalation;
}

// Build Back Risk (CFE-4): predicate for the `customer.flag.updated` live-refresh
// bind — refetch the work-order flag rollup only when the broadcast's SR set includes
// the SR the slide-out is currently showing. Exported for direct unit testing, since
// the pusher mock is a no-op and cannot simulate a bound event.
export const shouldRefetchFlagRollup = (
  serviceRequestIds: number[] | undefined,
  srId: string | number | undefined,
): boolean => !!serviceRequestIds?.includes(Number(srId));

export const FLAG_UPDATED_EVENT = 'customer.flag.updated';

// Build Back Risk (CFE-4): register the customer.flag.updated bind on the job
// channel. Exported so the EVENT NAME, the LD gating, and the SR-matched refetch
// decision are unit-testable — the slide-out mounts without a pusher in tests,
// so the bind cannot be exercised via a full render. No-op when the LD flag is
// off, so the surface is fully inert. Teardown is the shared channel unsubscribe.
export const bindCustomerFlagUpdated = (
  jobChannel: { bind: (event: string, handler: (data: unknown) => void) => void },
  params: {
    srId: string | number | undefined;
    rootNodeId: number | string | undefined;
    onRefetch: (rootNodeId: number | string) => void;
  },
): void => {
  const { srId, rootNodeId, onRefetch } = params;
  jobChannel.bind(FLAG_UPDATED_EVENT, (data: unknown) => {
    const { service_request_ids } = data as { service_request_ids?: number[]; flag?: unknown };
    if (shouldRefetchFlagRollup(service_request_ids, srId) && rootNodeId) {
      onRefetch(rootNodeId);
    }
  });
};

const RESIDENT_CAUSED_EVENTS = [
  'job.resident_caused.set',
  'job.resident_caused.edited',
  'job.resident_caused.removed',
  'job.resident_caused.deleted',
];

const JobSlideOut: React.FC = () => {
  const dispatch = useAppDispatch();
  const pusherRef = useRef<Pusher | null>(null);
  const { makeReadySlideout } = useFeatureFlags();

  const [userDetails] = useContext(AuthContext);
  const type = useAppSelector((state) => state?.jobDetails?.slideOut?.type);
  const currentlyViewingJobId = useAppSelector(
    (state) => state?.jobDetails?.slideOut?.openedJobDetailsId,
  );
  const needToRefetch = useAppSelector((state) => state?.jobDetails?.slideOut?.needToRefetch);
  const srId = useAppSelector((state) => state?.jobDetails?.slideOut?.srId);
  // Build Back Risk (CFE-4): the work-order ROOT node id (stored as slideOut.jobId)
  // is the key for the flag-rollup refetch on a live flag update.
  const rootNodeId = useAppSelector((state) => state?.jobDetails?.slideOut?.jobId);
  // Make-Ready jobs have no Build Back Risk flags, so the work-order flag rollup
  // is never fetched for them — neither on initial load nor on a live flag update.
  const serviceCategorySlug = useAppSelector(
    (state) => state?.jobDetails?.data?.service_category?.slug,
  );

  const isLoading = useAppSelector((state) => state?.jobDetails?.isLoading);

  const { getPusherInstance } = usePusherConfig();

  useEffect(() => {
    const partnerKey = (
      userDetails as Record<string, unknown>
    )?.partner as
      Record<string, unknown> | undefined;
    const partnerKeyValue = partnerKey?.partner_key as string | undefined;
    const pusher = partnerKeyValue ? getPusherInstance('job-slide-out') : undefined;
    // Build Back Risk (CFE-4): one controller for the live flag-rollup refetch,
    // aborted on cleanup so an in-flight refetch can never outlive the subscription.
    const controller = createAbortSignal();

    if (pusher && partnerKeyValue) {
      const channelName = `customer-service-request-jobs-${partnerKeyValue}`;
      const jobChannel = pusher.subscribe(
        channelName,
      ) as unknown as PusherChannel;

      const handleJobUpdate = (response: PusherResponse) => {
        const {
          event_for: eventFor,
          service_request_number: maintenanceRequestNumber,
          root_job_no: rootJobNumber,
        } = response;

        if (
          (maintenanceRequestNumber === currentlyViewingJobId)
          || (rootJobNumber === currentlyViewingJobId)
        ) {
          if (
            response?.data
            && Array.isArray(eventFor)
            && eventFor?.some(
              (item) => [
                'service_request',
                'root_job',
              ].includes(item),
            )
          ) {
            dispatch(updateSlideOutData(response?.data));
          } else {
            dispatch(updateSlideOut({
              needToRefetch: true,
            }));
          }
        }
      };

      jobChannel.bind(
        'customer.service_request_job.updated',
        (data: unknown) => handleJobUpdate(data as PusherResponse),
      );

      const handleActivityLogUpdate = (
        response: { data: ActivityLogEntry },
        idKey: string,
        eventType: string,
      ) => {
        const responseId = response?.data?.[idKey as keyof ActivityLogState['data']] || '';
        if (
          (responseId === currentlyViewingJobId)
          && (eventType === type)
        ) {
          dispatch(updateActivityLog({ data: response?.data }));
        }
      };

      // Define event configurations
      const activityLogEvents = [
        {
          eventName: 'job.activity_log',
          idKey: 'job_no',
          eventType: 'jobDetails',
        },
        {
          eventName: 'job.activity_log',
          idKey: 'service_request_no',
          eventType: 'serviceRequestDetails',
        },
        {
          eventName: 'job.activity_log.updated',
          idKey: 'job_no',
          eventType: 'jobDetails',
        },
        {
          eventName:
            'maintenance_request.activity_log',
          idKey: 'service_request_no',
          eventType: 'serviceRequestDetails',
        },
      ];

      // Bind events to jobChannel
      activityLogEvents.forEach(({ eventName, idKey, eventType }) => {
        jobChannel.bind(eventName, (response) => {
          handleActivityLogUpdate(
            response as { data: ActivityLogEntry },
            idKey,
            eventType,
          )
        },
        );
      });

      jobChannel.bind('job.activity_log', (response: unknown) => {
        const data = (response as { data: ActivityLogEntry })?.data;
        if (!data || !RESIDENT_CAUSED_EVENTS.includes(data.event)) return;

        const attrs = data.event_attributes;
        const isRemoved =
          data.event === 'job.resident_caused.removed' ||
          data.event === 'job.resident_caused.deleted' ||
          !attrs?.resident_cause_status;

        if (data.job_no === currentlyViewingJobId && type === 'jobDetails') {
          dispatch(
            updateResidentCausedFromPusher({
              jobId: Number(attrs?.job_id),
              isRemoved,
              entry: isRemoved
                ? undefined
                : {
                    value: attrs.resident_cause_status as 'partially' | 'yes',
                    reason: (attrs.reason as string) ?? '',
                    set_by_type:
                      attrs.changed_by === 'property_manager'
                        ? 'property_manager'
                        : 'admin',
                    set_at: data.created_at,
                    reason_for_change:
                      (attrs.reason_for_change as string | null) ?? null,
                    job_id: Number(attrs.job_id),
                  },
            }),
          );
        }

        window.dispatchEvent(
          new CustomEvent('resident-responsibility-updated', {
            detail: {
              jobId: Number(attrs?.job_id),
              is_resident_caused_problem: isRemoved
                ? ''
                : (attrs?.resident_cause_status ?? ''),
              resident_problem_description: isRemoved
                ? []
                : [(attrs?.reason as string) ?? ''],
            },
          }),
        );
      });

      jobChannel.bind('customer.property_escalation.updated', (data: unknown) => {
        const { service_request_ids, property_escalation } = data as PropertyEscalationUpdatedPayload;
        if (service_request_ids?.includes(Number(srId))) {
          dispatch(updatePropertyEscalation({ property_escalation }));
        }
      });

      // Build Back Risk (CFE-4): mirror the escalation bind above — on a flag set or
      // remove, refetch the open work order's flag rollup (keyed by the root node id)
      // so the chip + summary banner update live. Only when the broadcast's SR set
      // includes the SR currently shown, only when the LD flag is on, and only when a
      // root node id is present (otherwise no-op).
      bindCustomerFlagUpdated(jobChannel, {
        srId,
        rootNodeId,
        onRefetch: (id) => {
          if (makeReadySlideout && isMakeReadyServiceCategory(undefined, serviceCategorySlug)) return;
          dispatch(fetchWorkOrderFlags(id, controller.signal)({}));
        },
      });
    }
    return () => {
      controller?.abort();
      if (pusher && partnerKeyValue) {
        pusher.unsubscribe(`customer-service-request-jobs-${partnerKeyValue}`);
      }
    };
  }, [
    currentlyViewingJobId,
    rootNodeId,
    srId,
    serviceCategorySlug,
    dispatch,
    getPusherInstance,
    type,
    makeReadySlideout,
    (userDetails as Record<string, unknown>)?.partner
  ]);

  /**
   * Closes the job details slide-out panel by removing any query parameters
   *  related to job or service request details,
   * and dispatching actions to clear the slide-out state.
   */

  const closeJobDetailsSlideOut = useCallback(() => {
    const url = new URL(window?.location?.href);

    if (url?.searchParams?.get('jobId') || url?.searchParams?.get('mr')) {
      url?.searchParams?.delete('mr');
      url?.searchParams?.delete('jobId');
      window?.history?.pushState({}, '', url);
    }
    dispatch(clearSlideOut());
  }, [dispatch]);

  const setChatConfig = useCallback((res: Record<string, unknown>) => {
    const url = new URL(window?.location?.href);

    const groupId = url?.searchParams?.get('groupId');
    const activeTab = url?.searchParams?.get('activeTab');
    if (groupId && activeTab) {
      dispatch(updateSlideOut({
        activeTab,
      }));
      dispatch(updateMessage({
        activeThreadId: Number(groupId),
      }));
      url?.searchParams?.delete('groupId');
      url?.searchParams?.delete('activeTab');
      window?.history?.pushState({}, '', url);
    } else {
      const customerMessage = res?.customer_message as Record<string, unknown> | undefined;
      dispatch(updateMessage({
        activeThreadId: customerMessage?.default_group_id,
      }));
    }
  }, [dispatch]);

  const fetchJobData = useCallback((
    signal: AbortSignal,
    needToClear = false,
  ) => {
    // clear the previous job details and activity log data
    if (needToClear) {
      dispatch(clearJobDetails());
    }

    dispatch(updateSlideOut({ type }));
    if (type === 'jobDetails') {
      dispatch(fetchJobDetails(currentlyViewingJobId, signal)({})).unwrap().then((
        rawRes: unknown,
      ) => {
        const res = rawRes as Record<string, unknown>;
        dispatch(updateSlideOut({
          srId: String(res?.service_request_id ?? ''),
          jobId: String(res?.root_node_id ?? ''),
        }));
        setChatConfig(res);
        // Build Back Risk: fetch the work-order flag rollup keyed by the
        // work-order root node id (jobDetails mode only). Make-Ready jobs have
        // no Build Back Risk flags, so skip the call for them entirely.
        const rootNodeId = res?.root_node_id as number | string | undefined;
        const responseServiceCategorySlug = (
          res?.service_category as { slug?: string } | undefined
        )?.slug;
        if (
          rootNodeId !== undefined
          && rootNodeId !== null
          && !(makeReadySlideout && isMakeReadyServiceCategory(undefined, responseServiceCategorySlug))
        ) {
          dispatch(fetchWorkOrderFlags(rootNodeId, signal)({}));
        }
      }).catch((e: Record<string, unknown>) => {
        if (e?.actionType !== 'canceled') {
          closeJobDetailsSlideOut();
          toast(
            (e?.error as string)
            || (e?.errors as
              Record<string, string[]>
            )?.general?.[0]
            || 'Something went wrong',
            { type: 'error' },
          );
        }
      });
    } else if (type === 'serviceRequestDetails') {
      dispatch(
        fetchServiceRequestDetails(currentlyViewingJobId, signal)({}),
      ).unwrap().then(
        (rawRes: unknown) => {
          const res = rawRes as Record<string, unknown>;
          dispatch(updateSlideOut({
            srId: String(res?.service_request_id ?? ''),
            jobId: '',
          }));
          setChatConfig(res);
        },
      ).catch((e) => {
        if (e?.actionType !== 'canceled') {
          closeJobDetailsSlideOut();
          toast(
            e?.errors?.general?.[0] || 'Something went wrong',
            { type: 'error' },
          );
        }
      });
    }
  }, [
    closeJobDetailsSlideOut,
    currentlyViewingJobId,
    dispatch,
    setChatConfig,
    type,
    makeReadySlideout,
  ]);

  useEffect(() => {
    const controller = createAbortSignal();
    fetchJobData(controller?.signal, true);
    return () => {
      controller?.abort();
    };
  }, [fetchJobData]);

  useEffect(() => {
    const controller = createAbortSignal();
    if (needToRefetch) {
      // does not need to clear data when pusher is triggered otherwise blank page will appear
      fetchJobData(controller?.signal, false);
    }
    return () => {
      controller?.abort();
    };
  }, [fetchJobData, needToRefetch]);

  useEffect(() => {
    const partnerKey = userDetails?.partner?.partner_key;
    if (partnerKey) {
      pusherRef.current = getPusherInstance('pusherInstanceForPmApi') ?? null;

      const msgChannel = ['private-customer-chat-pusher', partnerKey].join('-');
      const customerMessageChannel = pusherRef.current?.subscribe(msgChannel);
      [
        'customer.chat.created',
        'customer.chat.unread',
        'customer.chat.unread-count',
      ].forEach((event) => {
        customerMessageChannel?.bind(event, (data: PusherMessagePayload['data']) => {
          dispatch(updateMessageOnPusher({ event, data }));
          if (event === 'customer.chat.unread-count') {
            dispatch(updateOpenJobs({
              data,
            }));
            // Re-emit for pages with local list state (open/closed make-ready
            // boards) so they share this proven connection instead of each
            // opening their own — same pattern as the escalation events.
            window.dispatchEvent(new CustomEvent('customer-chat.unread-count', { detail: data }));
          }
        },
        );
      });
    }
    return () => {
      if (pusherRef?.current) {
        pusherRef?.current?.disconnect();
      }
    };
  }, [dispatch, getPusherInstance, (userDetails as Record<string, unknown>)?.partner]);

  return (
    <Offcanvas
      isOpen={!isEmpty(currentlyViewingJobId)}
      toggle={closeJobDetailsSlideOut}
      direction="end"
      className={`job-details-slide ${type === 'serviceRequestDetails' ? 'mr-request' : ''}`}
    >
      <Header closeJobDetailsSlideOut={closeJobDetailsSlideOut} />
      <OffcanvasBody>
        <div className="job-content-wrapper" id="activityLogScroll">
          <BasicInfo />
          <div className="job-content-tabs">
            {isLoading
              ? <div className="common-loading-lg mb-2" />
              : <NavTable />}
          </div>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};
export default JobSlideOut;